// ReadUI.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import useMediaSession from "../components/useMediaSession";
import Listen from "../components/Listen"; // Ensure this import path is correct

const ReadUI = ({ book, textColor, bookId }) => { // audioRef is not a prop, it's created locally
    const audioRef = useRef(null); // Create the ref here
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showTimerModal, setShowTimerModal] = useState(false);
    const [showCarModeModal, setShowCarModeModal] = useState(false);
    const [showAudioListModal, setShowAudioListModal] = useState(false);
    const [sleepTime, setSleepTime] = useState(5);

    useEffect(() => {
        audioRef.current = new Audio();
    }, []); // Empty dependency array means this runs once on mount

    useEffect(() => {
        if (!book?.audioUrls?.length) return;
        const audio = audioRef.current;

        if (audio.src !== book.audioUrls[currentIndex]) {
            audio.src = book.audioUrls[currentIndex];
            audio.currentTime = 0;
            if (isPlaying) { // Only play if already playing or if newly set to play
                audio.play().catch((err) => console.error("Audio play error:", err));
            }
        } else if (isPlaying) {
            audio.play().catch((err) => console.error("Audio play error:", err));
        }


        // Cleanup function for when the component unmounts or dependencies change
        return () => {
            if (audio) {
                audio.pause();
                // Optionally reset current time if you want to start from beginning next time
                // audio.currentTime = 0;
            }
        };
    }, [currentIndex, book, isPlaying]); // Include audioRef in dependencies if it could change, though with useRef it generally won't.

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return; // Ensure audio element exists

        const updateProgress = () => {
            setCurrentTime(audio.currentTime);
            setDuration(audio.duration || 0);
        };

        // Note: The handleNext in this useEffect is for audio 'ended' event,
        // it's different from the useCallback version for button clicks.
        const handleAudioEnded = () => {
            setCurrentIndex((prev) => {
                if (prev < book.audioUrls.length - 1) {
                    return prev + 1;
                } else {
                    setIsPlaying(false); // Stop playing if it's the last track
                    return 0; // Or keep it at the last index, depending on desired behavior
                }
            });
            // If you want to automatically play the next track, set isPlaying to true here
            // setIsPlaying(true); // This might cause a double play if the previous useEffect also tries to play
        };


        audio.addEventListener("timeupdate", updateProgress);
        audio.addEventListener("ended", handleAudioEnded);

        return () => {
            audio.removeEventListener("timeupdate", updateProgress);
            audio.removeEventListener("ended", handleAudioEnded);
        };
    }, [book, audioRef, setIsPlaying]); // Add setIsPlaying to dependencies


    useEffect(() => {
        if (sleepTime > 0 && isPlaying) { // Only set timer if playing
            const timer = setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.pause();
                }
                setIsPlaying(false);
                setShowTimerModal(false); // Close modal after timer
            }, sleepTime * 60000);
            return () => clearTimeout(timer);
        } else if (sleepTime === 0) {
            // If sleepTime is set to 0, clear any existing timers
            // (though the component unmount/re-render might handle this)
        }
    }, [sleepTime, isPlaying, audioRef]); // Include isPlaying in dependencies

    const togglePlay = useCallback(() => {
        if (!book?.audioUrls?.length || !audioRef.current) return;
        const audio = audioRef.current;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch((err) => console.error("Audio play error:", err));
        }
        setIsPlaying((prev) => !prev);
    }, [isPlaying, book, audioRef]);

    const handleNext = useCallback(() => {
        if (book?.audioUrls?.length) {
            setCurrentIndex((prev) => (prev < book.audioUrls.length - 1 ? prev + 1 : 0));
            setIsPlaying(true); // Automatically play next track
        }
    }, [book]);

    const handlePrev = useCallback(() => {
        if (book?.audioUrls?.length) {
            setCurrentIndex((prev) => (prev > 0 ? prev - 1 : book.audioUrls.length - 1));
            setIsPlaying(true); // Automatically play previous track
        }
    }, [book]);

    const toggleAudioListModal = useCallback(() => setShowAudioListModal((prev) => !prev), []); // No dependencies needed

    const formatTime = useCallback((time) => {
        if (isNaN(time) || time < 0) return "0:00"; // Handle invalid time
        const minutes = Math.floor(time / 60);
        const seconds = String(Math.floor(time % 60)).padStart(2, "0");
        return `${minutes}:${seconds}`;
    }, []);

    useMediaSession({
        title: book.title,
        artist: book.author,
        albumArt: book.imageUrl,
        isPlaying,
        onPlay: togglePlay,
        onPause: togglePlay,
        onNext: handleNext,
        onPrevious: handlePrev,
    });

    return (
        <>
            <Listen
                book={book}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                setIsPlaying={setIsPlaying}
                audioRef={audioRef}
                formatTime={formatTime}
                currentTime={currentTime}
                duration={duration}
                setCurrentTime={setCurrentTime}
                handlePrev={handlePrev}
                togglePlay={togglePlay}
                isPlaying={isPlaying}
                handleNext={handleNext}
                setShowTimerModal={setShowTimerModal}
                setShowCarModeModal={setShowCarModeModal}
                showTimerModal={showTimerModal}
                setSleepTime={setSleepTime}
                sleepTime={sleepTime}
                showCarModeModal={showCarModeModal}
                toggleAudioListModal={toggleAudioListModal}
                showAudioListModal={showAudioListModal}
                textColor={textColor}
                bookId={bookId}
            />
        </>
    );
};

export default ReadUI;